import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { addActivity } from '../utils/activities';
import {
  BACKUP_FILTER_OPTIONS,
  DEFAULT_BACKUP_FILTER,
  downloadBackupWorkbook,
  getBackupSnapshot,
} from '../utils/backupWorkbook';
import './BackupCenter.css';

function createEmptySnapshot() {
  return {
    generatedAt: new Date().toISOString(),
    filter: {
      filterType: 'all',
      filterLabel: 'All Data',
      periodLabel: 'Entire dataset',
      rangeLabel: 'All records',
      startDate: '',
      endDate: '',
    },
    counts: {
      loans: 0,
      customers: 0,
      lenders: 0,
      lenderLogins: 0,
      enquiries: 0,
      leads: 0,
      users: 0,
      activities: 0,
      flowStages: 0,
    },
    sheets: [],
  };
}

function BackupCenter() {
  const { user } = useAuth();
  const [filters, setFilters] = useState(DEFAULT_BACKUP_FILTER);
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_BACKUP_FILTER);
  const [snapshot, setSnapshot] = useState(createEmptySnapshot);
  const [loadingSnapshot, setLoadingSnapshot] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageTone, setMessageTone] = useState('info');

  const validateFilters = (candidateFilters = filters) => {
    const filterType = String(candidateFilters?.filterType || 'all').trim().toLowerCase();
    const normalizedFilterType = BACKUP_FILTER_OPTIONS.some((option) => option.value === filterType) ? filterType : 'all';

    if (normalizedFilterType !== 'custom') {
      return {
        filterType: normalizedFilterType,
        startDate: '',
        endDate: '',
      };
    }

    const startDate = String(candidateFilters?.startDate || '').trim();
    const endDate = String(candidateFilters?.endDate || '').trim();

    if (!startDate || !endDate) {
      throw new Error('Select both start and end date for a custom backup range.');
    }

    if (startDate > endDate) {
      throw new Error('Start date cannot be after end date.');
    }

    return {
      filterType: normalizedFilterType,
      startDate,
      endDate,
    };
  };

  const loadSnapshot = async (candidateFilters = appliedFilters, options = {}) => {
    const { successMessage = '', tone = 'info', silent = false } = options;
    const validatedFilters = validateFilters(candidateFilters);

    setLoadingSnapshot(true);
    if (!silent) {
      setMessage('');
    }

    try {
      const nextSnapshot = await getBackupSnapshot(validatedFilters);
      setSnapshot(nextSnapshot || createEmptySnapshot());
      setAppliedFilters(validatedFilters);

      if (successMessage) {
        setMessageTone(tone);
        setMessage(successMessage);
      }

      return nextSnapshot;
    } catch (error) {
      const nextMessage = error?.message || 'Failed to load the backup snapshot.';
      setMessageTone('error');
      setMessage(nextMessage);
      throw error;
    } finally {
      setLoadingSnapshot(false);
    }
  };

  useEffect(() => {
    if (!user || user.role !== 'superuser') {
      return undefined;
    }

    let isMounted = true;

    const initialLoad = async () => {
      try {
        await loadSnapshot(DEFAULT_BACKUP_FILTER, { silent: true });
      } catch (error) {
        if (!isMounted) {
          return;
        }
      }
    };

    initialLoad();

    const sync = () => {
      if (!isMounted) {
        return;
      }

      loadSnapshot(appliedFilters, { silent: true }).catch(() => {});
    };

    window.addEventListener('focus', sync);
    window.addEventListener('auth:changed', sync);

    return () => {
      isMounted = false;
      window.removeEventListener('focus', sync);
      window.removeEventListener('auth:changed', sync);
    };
  }, [appliedFilters, user]);

  if (!user || user.role !== 'superuser') {
    return (
      <div className="backup-page">
        <div className="backup-hero">
          <div>
            <h1>Backup Center</h1>
            <p>Access denied. Superuser only.</p>
          </div>
        </div>
      </div>
    );
  }

  const handleApplyFilter = async () => {
    try {
      await loadSnapshot(filters, {
        successMessage: 'Backup snapshot updated for the selected date filter.',
      });
    } catch (error) {
      // Message is already surfaced in loadSnapshot.
    }
  };

  const handleResetFilters = async () => {
    setFilters(DEFAULT_BACKUP_FILTER);
    try {
      await loadSnapshot(DEFAULT_BACKUP_FILTER, {
        successMessage: 'Backup snapshot reset to all data.',
      });
    } catch (error) {
      // Message is already surfaced in loadSnapshot.
    }
  };

  const handleDownload = async () => {
    let nextSnapshot = null;

    try {
      const validatedFilters = validateFilters(filters);
      setDownloading(true);
      setMessage('');

      const { fileName } = await downloadBackupWorkbook(validatedFilters);
      nextSnapshot = await loadSnapshot(validatedFilters, { silent: true });
      setMessageTone('success');
      setMessage(`Backup downloaded as ${fileName}`);

      try {
        await addActivity({
          type: 'backup_downloaded',
          actor: user?.username || 'system',
          message: `${fileName} downloaded from Backup Center`,
          meta: {
            fileName,
            generatedAt: nextSnapshot?.generatedAt || new Date().toISOString(),
            filterType: validatedFilters.filterType,
            startDate: validatedFilters.startDate || '',
            endDate: validatedFilters.endDate || '',
          },
        });
      } catch (error) {
        // Ignore activity failures and keep backup download successful.
      }
    } catch (error) {
      setMessageTone('error');
      setMessage(error?.message || 'Failed to generate the Excel backup.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="backup-page">
      <div className="backup-hero">
        <div>
          <h1>Backup Center</h1>
          <p>
            Generate a server-side Excel backup of your MongoDB data using all records,
            the current month, current quarter, current year, or a custom created-at date range.
          </p>
        </div>
      </div>

      <section className="backup-filter-panel">
        <div className="backup-panel-header">
          <h2>Backup Filter</h2>
          <span>{snapshot.filter?.filterLabel || 'All Data'}</span>
        </div>
        <div className="backup-filter-grid">
          <label className="backup-field">
            <span>Filter Type</span>
            <select
              className="backup-select"
              value={filters.filterType}
              onChange={(e) => setFilters({ ...filters, filterType: e.target.value })}
            >
              {BACKUP_FILTER_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>

          {filters.filterType === 'custom' ? (
            <>
              <label className="backup-field">
                <span>From Date</span>
                <input
                  className="backup-input"
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                />
              </label>
              <label className="backup-field">
                <span>To Date</span>
                <input
                  className="backup-input"
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                />
              </label>
            </>
          ) : null}
        </div>

        <div className="backup-chip-row">
          <div className="backup-chip">
            <span>Applied Filter</span>
            <strong>{snapshot.filter?.filterLabel || 'All Data'}</strong>
          </div>
          <div className="backup-chip">
            <span>Period</span>
            <strong>{snapshot.filter?.periodLabel || 'Entire dataset'}</strong>
          </div>
          <div className="backup-chip">
            <span>Date Range</span>
            <strong>{snapshot.filter?.rangeLabel || 'All records'}</strong>
          </div>
        </div>

        <div className="backup-filter-actions">
          <button type="button" className="backup-secondary-btn" onClick={handleApplyFilter} disabled={loadingSnapshot}>
            {loadingSnapshot ? 'Refreshing Snapshot...' : 'Apply Filter'}
          </button>
          <button type="button" className="backup-primary-btn" onClick={handleDownload} disabled={downloading}>
            {downloading ? 'Preparing Filtered Backup...' : 'Download Filtered Backup'}
          </button>
          <button type="button" className="backup-secondary-btn" onClick={handleResetFilters} disabled={loadingSnapshot || downloading}>
            Reset To All Data
          </button>
        </div>
        <div className="backup-filter-note">
          The downloaded Excel file will use the filter selected above.
        </div>
      </section>

      {message ? <div className={`backup-banner ${messageTone === 'error' ? 'is-error' : 'is-success'}`}>{message}</div> : null}

      <div className="backup-stats-grid">
        <div className="backup-stat-card">
          <span className="backup-stat-label">Loans In Backup</span>
          <strong className="backup-stat-value">{snapshot.counts.loans}</strong>
        </div>
        <div className="backup-stat-card">
          <span className="backup-stat-label">Customers</span>
          <strong className="backup-stat-value">{snapshot.counts.customers}</strong>
        </div>
        <div className="backup-stat-card">
          <span className="backup-stat-label">Lenders Synced</span>
          <strong className="backup-stat-value">{snapshot.counts.lenders}</strong>
        </div>
        <div className="backup-stat-card">
          <span className="backup-stat-label">Lender Logins</span>
          <strong className="backup-stat-value">{snapshot.counts.lenderLogins}</strong>
        </div>
        <div className="backup-stat-card">
          <span className="backup-stat-label">Enquiries</span>
          <strong className="backup-stat-value">{snapshot.counts.enquiries}</strong>
        </div>
        <div className="backup-stat-card">
          <span className="backup-stat-label">Leads</span>
          <strong className="backup-stat-value">{snapshot.counts.leads}</strong>
        </div>
        <div className="backup-stat-card">
          <span className="backup-stat-label">Users</span>
          <strong className="backup-stat-value">{snapshot.counts.users}</strong>
        </div>
        <div className="backup-stat-card">
          <span className="backup-stat-label">Activities</span>
          <strong className="backup-stat-value">{snapshot.counts.activities}</strong>
        </div>
      </div>

      <div className="backup-content-grid">
        <section className="backup-panel">
          <div className="backup-panel-header">
            <h2>Workbook Sheets</h2>
            <span>{snapshot.sheets.length} sheets</span>
          </div>
          <div className="backup-sheet-list">
            {loadingSnapshot && snapshot.sheets.length === 0 ? (
              <div className="backup-panel-loading">Loading backup snapshot...</div>
            ) : snapshot.sheets.map((sheet) => (
              <div key={sheet.name} className="backup-sheet-row">
                <div>
                  <strong>{sheet.label}</strong>
                  <p>{sheet.name} • {sheet.columnCount} columns</p>
                </div>
                <span>{sheet.rowCount} rows</span>
              </div>
            ))}
          </div>
        </section>

        <section className="backup-panel emphasis">
          <div className="backup-panel-header">
            <h2>Export Notes</h2>
            <span>{snapshot.counts.flowStages} stages</span>
          </div>
          <ul className="backup-notes">
            <li>The backup is generated on the server from MongoDB, not from browser cache.</li>
            <li>Monthly, quarterly, yearly, and custom exports filter each collection using its `createdAt` value.</li>
            <li>The workbook includes summary, loan flow, customers, lenders, logins, enquiries, leads, users, and activities sheets.</li>
            <li>Custom date ranges are validated before the export is generated.</li>
          </ul>
          <div className="backup-meta">
            Last snapshot prepared: <strong>{new Date(snapshot.generatedAt).toLocaleString()}</strong>
          </div>
        </section>
      </div>
    </div>
  );
}

export default BackupCenter;
